import { z } from 'zod';

/**
 * Court diagrams. Coordinates are in metres on a FIBA court (15 m wide,
 * 28 m long). x runs across the court (0 = left sideline), y runs from the
 * baseline under the basket (0) towards halfway (14) and the far baseline (28).
 * A half-court diagram only uses y 0–14.
 */

export const COURT_WIDTH = 15;
export const HALF_LENGTH = 14;
export const FULL_LENGTH = 28;

const coord = z.number().finite();
const point = z.object({ x: coord.min(-1).max(16), y: coord.min(-1).max(29) });

const at = { id: z.string().min(1).max(40), x: coord.min(-1).max(16), y: coord.min(-1).max(29) };

export const diagramObject = z.discriminatedUnion('kind', [
  z.object({ ...at, kind: z.literal('offence'), label: z.string().trim().max(3) }),
  z.object({ ...at, kind: z.literal('defence'), label: z.string().trim().max(3) }),
  z.object({ ...at, kind: z.literal('coach') }),
  z.object({ ...at, kind: z.literal('ball') }),
  z.object({ ...at, kind: z.literal('cone') }),
  z.object({ ...at, kind: z.literal('text'), text: z.string().trim().min(1).max(40) }),
  z.object({
    id: z.string().min(1).max(40),
    kind: z.enum(['cut', 'pass', 'dribble', 'screen']),
    points: z.array(point).min(2).max(8),
  }),
]);
export type DiagramObject = z.infer<typeof diagramObject>;
export type LineKind = 'cut' | 'pass' | 'dribble' | 'screen';
export type LineObject = Extract<DiagramObject, { points: unknown }>;

export const diagramStep = z.object({
  id: z.string().min(1).max(40),
  caption: z
    .string()
    .trim()
    .max(200, 'Keep step captions under 200 characters.')
    .nullish()
    .transform((v) => v || null),
  objects: z.array(diagramObject).max(60, 'That diagram has too many things on it. Use up to 60.'),
});
export type DiagramStep = z.infer<typeof diagramStep>;

export const diagram = z.object({
  court: z.enum(['half', 'full']),
  steps: z.array(diagramStep).min(1).max(4, 'Use up to 4 steps.'),
});
export type Diagram = z.infer<typeof diagram>;

export const LINE_LABELS: Record<LineKind, string> = {
  cut: 'Cut or move',
  pass: 'Pass',
  dribble: 'Dribble',
  screen: 'Screen',
};

export function isLine(o: DiagramObject): o is LineObject {
  return 'points' in o;
}

export function diagramIsEmpty(d: Diagram | null | undefined) {
  return !d || d.steps.every((s) => !s.objects.length);
}
